/**
 * Per-seat billing domain types — buy-upfront.
 *
 * Plans can be sold by the seat: the customer buys N seats and the subscription
 * quantity is N. Users (`Active` + `PendingVerification`) fill those seats;
 * adding or removing one is free and changes no billing. The seat count itself
 * is changed explicitly through "manage seats".
 */

/** One line of the add-a-seat invoice preview. */
export interface AddOnInvoiceLineItem {
  description: string;
  /** Human-readable range, e.g. "28 Jul 2026 – 28 Jul 2027". */
  period: string;
  amount: number;
}

/**
 * Cost of changing to a given seat count — `GET .../seat/preview/{count}`,
 * where `{count}` is the target *total* seats, not a delta. Preview only —
 * nothing is charged. Works both ways: increasing `count` past the account's
 * current seats prices the added seats' remaining time (positive
 * `amountDueNow`/`total`); decreasing it prices the released seats' unused
 * time as a credit (negative `amountDueNow`/`total` — render as a credit,
 * never as an error).
 *
 * Superseded the old param-less "preview adding one seat" endpoint, on which
 * `nextRenewalDate`/`nextRenewalAmount` were confirmed wrong (today's date
 * instead of the subscription's; the plan's bare unit price instead of the
 * real total). On this endpoint both are correct for whatever `count` was
 * requested — `nextRenewalAmount` is the account's real next full-cycle bill
 * at that seat count, `nextRenewalDate` is when it lands.
 */
export interface AddOnInvoicePreview {
  /** Uppercase ISO code, e.g. "USD". */
  currency: string;
  lineItems: AddOnInvoiceLineItem[];
  /** Prorated amount charged today; negative on a decrease (a credit). */
  amountDueNow: number;
  tax: number;
  /** Pre-credit invoice value; negative on a decrease. */
  total: number;
  /** "yyyy-MM-dd" of the next full charge, at the requested seat count. */
  nextRenewalDate: string;
  /** The account's real next full-cycle bill at the requested seat count. */
  nextRenewalAmount: number;
  /** Epoch seconds this proration was anchored to. Debug/internal only. */
  prorationDate: number;
}

/** The three states `GET /billing/fetch/subscription/state` can report. */
export const subscriptionState = {
  active: "ACTIVE",
  cancelled: "CANCELLED",
  neverSubscribed: "NEVER_SUBSCRIBED",
} as const;

export type SubscriptionStateValue =
  (typeof subscriptionState)[keyof typeof subscriptionState];

/**
 * `GET /billing/fetch/subscription/state`. Safe to call on any screen — unlike
 * the other billing endpoints it never errors for an unsubscribed account, so
 * it's the right thing to gate them on rather than calling them blind.
 *
 * `planName`/`planId` are the plan **currently in effect**, not necessarily the
 * one that was cancelled — an expired paid account auto-converts to a lifetime
 * free plan, and this reports that plan, not the one it replaced.
 */
export interface SubscriptionState {
  state: SubscriptionStateValue;
  subscribed: boolean;
  planId: string;
  planName: string;
  /** "yyyy-MM-dd" */
  validTill: string;
  planValid: boolean;
}

/**
 * App-level billing flags from `GET /billing/fetch/config`.
 *
 * Per-seat needs **both** gates: this flag and the plan's own
 * `metadata.perSeatBilling`. With the app flag off the backend bills flat no
 * matter what a plan says, so the seat UI must stay hidden.
 */
export interface BillingConfig {
  perSeatBillingEnabled: boolean;
  /** An offer schedule is in play; plan changes then aren't prorated. */
  offerEnabled: boolean;
}

/**
 * Billing overview from `GET /billing/fetch/summary`.
 *
 * Downgrades and seat reductions don't refund the card — they leave a credit on
 * the account that Stripe applies to later invoices by itself. That is why a
 * change can charge nothing at all, so the credit has to be visible or the
 * customer is left guessing where a `$0.00` came from.
 */
export interface BillingSummary {
  /** Uppercase ISO code, e.g. "USD". */
  currency: string;
  planName: string;
  /** Price of a single seat. */
  unitPrice: number;
  /** Seats **bought** — what the account pays for, and the billing basis. */
  seats: number;
  /** Users occupying those seats. Invariant: `activeUsers ≤ seats ≤ cap`. */
  activeUsers: number;
  /**
   * The most seats this plan may be bought with. Bounds the seat picker only —
   * never the seat count and never the price basis; on an uncapped plan it is
   * a sentinel like 99999.
   */
  cap: number;
  /** The per-cycle bill: `seats × unitPrice`. */
  subtotal: number;
  /** The whole credit saved on the account — the headline figure to show. */
  creditBalance: number;
  /** The slice of that credit the next renewal will consume. */
  creditApplied: number;
  /** Net due next renewal: `max(0, subtotal − creditApplied)`. */
  nextRenewalAmount: number;
  /** "yyyy-MM-dd". */
  nextRenewalDate: string;
  /*
    Subscription discount (e.g. a sale code bought with). Only sent once the
    backend prices the summary off Stripe's upcoming invoice, which also makes
    an annual plan's amounts real yearly figures. Absent from older backends;
    null when there is no discount.
  */
  /** This cycle's bill after the discount, before credit. */
  discountedSubtotal?: number | null;
  /** Whole-percent discount on the subscription. */
  discountPercent?: number | null;
  /** Last day the discount applies, "yyyy-MM-dd"; null if it never ends. */
  discountEndsOn?: string | null;
  /** Per-cycle bill once the discount ends. */
  regularAmountAfterDiscount?: number | null;
}

/** A user offered for keep/deactivate selection during a downgrade. */
export interface SeatReductionUser {
  id: number;
  email: string;
  name: string;
  username: string;
  status: string;
}

/**
 * Returned by the seat check, and as the body of a `409` from the plan switch.
 * `users` is present **only** when a reduction is required
 * (`activeCount > keepCount`).
 */
export interface SeatReductionRequired {
  reason: string;
  planId: string;
  /** How many users the target plan allows — the number that must be kept. */
  keepCount: number;
  /** Billable users today. */
  activeCount: number;
  users?: SeatReductionUser[];
}

/** One line of the plan-switch invoice preview's raw Stripe breakdown. */
export interface PlanSwitchPreviewLineItem {
  description: string;
  /** Human-readable range, e.g. "22 Aug 2026 – 22 Sep 2026". */
  period: string;
  amount: number;
}

/**
 * `GET /billing/fetch/plan/{planId}/switch/preview` — the exact cost of
 * switching to `planId`, before anything is charged.
 *
 * The switch itself is anchor-reset: the new plan is always charged in full
 * for a fresh billing cycle starting today (`chargeForNewPlan`), and
 * `creditForUnusedTime` on the outgoing plan is applied against that — the
 * renewal date moves to today plus one cycle (`nextRenewalDate`), it never
 * stays at the old plan's renewal date. That holds whether or not the switch
 * is `prorated`; `prorated: false` (non-per-seat app, an offer schedule on the
 * account, or no active subscription yet) just means there is no outgoing
 * plan to credit, not that the anchor is preserved.
 *
 * `chargeForNewPlan` / `creditForUnusedTime` / `amountDueNow` are the numbers
 * to build the checkout UI from; `lineItems` is Stripe's raw breakdown and
 * only worth showing as extra detail.
 */
export interface PlanSwitchPreview {
  /** Uppercase ISO code, e.g. "USD". */
  currency: string;
  planId: string;
  planName: string;
  /** Clamped to the plan's cap — always render this, not the count requested. */
  seats: number;
  unitPrice: number;
  chargeForNewPlan: number;
  creditForUnusedTime: number;
  /** Account's Stripe credit balance before this charge. */
  creditBalance: number;
  /** Slice of that balance this charge actually consumes. */
  creditApplied: number;
  /** `chargeForNewPlan − creditForUnusedTime`, before `creditApplied`. */
  total: number;
  /** What to charge/display as "Pay now" — floored at 0. */
  amountDueNow: number;
  /** "yyyy-MM-dd" of the next full charge. */
  nextRenewalDate: string;
  /** `seats × unitPrice`, charged on `nextRenewalDate`. */
  nextRenewalAmount: number;
  prorated: boolean;
  lineItems: PlanSwitchPreviewLineItem[];
}

/** Optional body for a plan switch; either field may stand on its own. */
export interface SeatChangeRequest {
  /** Exactly `keepCount` ids, each from the returned `users`. */
  keepUserIds?: number[];
  /**
   * Purchased seats to set on the new plan. The backend clamps it to
   * [active users, plan cap]; omitting it carries the current count over.
   */
  seats?: number;
}

/** User lifecycle states the seat rules key off. */
export const userStatus = {
  active: "Active",
  inactive: "Inactive",
  /** Invited but not yet accepted — billable from the moment of invite. */
  pendingVerification: "PendingVerification",
  locked: "Locked",
} as const;

export type UserStatus = (typeof userStatus)[keyof typeof userStatus];

/** Statuses that occupy a seat (invited users hold one from the invite). */
export const isBillableStatus = (status?: string) =>
  status === userStatus.active || status === userStatus.pendingVerification;
